import { createContext, useContext, useLayoutEffect, useRef } from 'react';
import { HttpApiError, isTransientRequestFailure } from '@/lib/api-error';

// The browser half of the server-composed body (server-body.ts). A composed
// response serves the page's text in `#server-body` and the React mount as
// `<div id="root" hidden>`. React boots into the hidden root exactly as it
// does everywhere else, so the Clerk gate's loading state and the route's
// skeletons render where nobody sees them. When the page is finished, the
// handoff removes the server body and reveals the root, once, before the next
// paint. A response without `#server-body` leaves `#root` visible, so every
// uncomposed route boots exactly as before and every call here is a no-op.

const SERVER_BODY_ID = 'server-body';
const ROOT_ID = 'root';
// Set on `#server-body` when the live page failed to load what it shows.
const KEPT_ATTRIBUTE = 'data-kept';

// Each API request made while the server body is on screen is cut off after
// this long without a response (`serverBodyRequestTimeoutMs`), so a hanging
// API fails the read and the server body is kept, instead of the page
// waiting on it until the deadline hands it off unfinished.
export const SERVER_BODY_REQUEST_TIMEOUT_MS = 4_000;

// The longest chain of reads a composed page waits on, one after another,
// before it is ready: the session probe in `_public`'s beforeLoad, then the
// entity in the route loader, then the resource page's related list, which
// needs the entity's first tag. None of them retries while the server body
// shows (`serverBodyRetry`).
const COMPOSED_PAGE_SEQUENTIAL_READS = 3;

// Rendering and the route's code chunk, around those reads.
const SERVER_BODY_DEADLINE_MARGIN_MS = 2_000;

// Measured from the router's mount (`useServerBodyDeadline`), not from boot:
// the font and Clerk gates before it are bounded on their own and cost the
// visitor nothing, since the server body is the page's content. By this
// point every read the page waits on has answered or timed out, so a page
// still not settled is stuck, not slow.
export const SERVER_BODY_DEADLINE_MS =
  SERVER_BODY_REQUEST_TIMEOUT_MS * COMPOSED_PAGE_SEQUENTIAL_READS + SERVER_BODY_DEADLINE_MARGIN_MS;

// The waits between a kept page's attempts to load its failed read again,
// each counted from the end of the attempt before it, so the last attempt
// starts about 65 s after the failure. The page is readable throughout, so
// these are slow; a success hands off to the live page.
export const SERVER_BODY_RECOVERY_DELAYS_MS: readonly number[] = [5_000, 15_000, 45_000];

// A kept page whose recovery attempts all failed is reported for this share
// of visitors. Every visitor of every composed page hits an outage that
// only humans reach (a proxy, DNS), so a tenth of them is still a burst of
// reports within minutes, while the scripted crawlers that both sit on a
// page for a minute and are refused /api/ by robots rules stay a trickle.
export const SERVER_BODY_UNRECOVERED_SAMPLE_RATE = 0.1;

export type Schedule = (run: () => void, delayMs: number) => () => void;

export type ServerBodyReport =
  | { readonly event: 'render.handoff_timeout'; readonly path: string }
  | { readonly event: 'render.server_body_kept'; readonly path: string; readonly status: number }
  | {
      readonly event: 'render.server_body_unrecovered';
      readonly path: string;
      readonly status: number | null;
    };

// What the handoff needs from the browser, provided once by main.tsx.
export interface ServerBodyEnvironment {
  readonly document: Document;
  readonly schedule: Schedule;
  readonly report: (report: ServerBodyReport) => void;
  readonly isOnline: () => boolean;
  // A uniform draw in [0, 1).
  readonly sample: () => number;
}

// Null when the document was not served with a server body, so every hook
// below is a no-op on an uncomposed boot.
const ServerBodyContext = createContext<ServerBodyEnvironment | null>(null);

export const ServerBodyProvider = ServerBodyContext.Provider;

// The result of trying a failed read again, as a query's `refetch` returns it.
export interface RetryOutcome {
  readonly isError: boolean;
  readonly error: unknown;
}

// The server body scrolls the window; the live page scrolls AppShell's
// `<main>`. A visitor who has read partway down would otherwise be thrown back
// to the top at the handoff, so the reading position moves across with them.
// Both bodies render the same text, so the anchor is the first block the
// visitor can see, matched by tag and text and put back at the same height.
const ANCHOR_BLOCKS = 'h1, h2, h3, h4, h5, h6, p, li, blockquote, pre';

interface ReadingAnchor {
  readonly tagName: string;
  readonly text: string;
  readonly top: number;
}

function normalizedText(element: Element): string {
  return (element.textContent ?? '').replace(/\s+/g, ' ').trim();
}

function readingAnchor(serverBody: HTMLElement, scrollY: number): ReadingAnchor | null {
  if (scrollY <= 0) return null;
  for (const block of serverBody.querySelectorAll(ANCHOR_BLOCKS)) {
    const rect = block.getBoundingClientRect();
    const text = normalizedText(block);
    if (rect.bottom > 0 && text !== '') return { tagName: block.tagName, text, top: rect.top };
  }
  return null;
}

function restoreReadingAnchor(root: HTMLElement, anchor: ReadingAnchor): void {
  const main = root.querySelector('main');
  if (main === null) return;
  for (const block of main.querySelectorAll(anchor.tagName)) {
    if (normalizedText(block) !== anchor.text) continue;
    main.scrollTop += block.getBoundingClientRect().top - anchor.top;
    return;
  }
}

export function isShowingServerBody(doc: Document): boolean {
  return doc.getElementById(SERVER_BODY_ID) !== null;
}

function isKept(doc: Document): boolean {
  return doc.getElementById(SERVER_BODY_ID)?.hasAttribute(KEPT_ATTRIBUTE) ?? false;
}

// True when this call performed the handoff.
export function handOffServerBody(doc: Document): boolean {
  const serverBody = doc.getElementById(SERVER_BODY_ID);
  if (serverBody === null) return false;
  const anchor = readingAnchor(serverBody, doc.defaultView?.scrollY ?? 0);
  serverBody.remove();
  const root = doc.getElementById(ROOT_ID);
  root?.removeAttribute('hidden');
  if (root !== null && anchor !== null) restoreReadingAnchor(root, anchor);
  return true;
}

// Marks the server body as the finished page and turns the deadline off for
// it. True when a server body was showing.
export function keepServerBody(doc: Document): boolean {
  const serverBody = doc.getElementById(SERVER_BODY_ID);
  if (serverBody === null) return false;
  serverBody.setAttribute(KEPT_ATTRIBUTE, '');
  return true;
}

// The app's query retry policy (app.tsx). While the server body is on
// screen a failed read is answered by keeping it (`useKeepServerBody`), so
// retrying first only delays that answer: with the default 1 + 2 + 4 s
// backoff a failing page could not settle before the deadline. This covers
// every read the hidden page makes, including the session probe, which the
// app's locale provider starts before the router's own guard asks for it.
// Checked at each failure, so once the page has handed off its queries retry
// as everywhere else.
export function serverBodyRetry(
  doc: Document,
  retry: (failureCount: number, error: unknown) => boolean,
): (failureCount: number, error: unknown) => boolean {
  return (failureCount, error) => !isShowingServerBody(doc) && retry(failureCount, error);
}

// The API client's request timeout (main.tsx): bounded while the server body
// is on screen, unbounded otherwise, as the browser leaves it.
export function serverBodyRequestTimeoutMs(doc: Document): number | null {
  return isShowingServerBody(doc) ? SERVER_BODY_REQUEST_TIMEOUT_MS : null;
}

function report(env: ServerBodyEnvironment, build: (path: string) => ServerBodyReport): void {
  env.report(build(env.document.location.pathname));
}

// If the page has not handed off by the deadline it hands off anyway and
// reports it, so a page that never settles cannot stay behind the server
// body. Returns the cancel.
export function armServerBodyDeadline(env: ServerBodyEnvironment): () => void {
  if (!isShowingServerBody(env.document)) return () => undefined;
  return env.schedule(() => {
    if (isKept(env.document)) return;
    if (handOffServerBody(env.document)) {
      report(env, (path) => ({ event: 'render.handoff_timeout', path }));
    }
  }, SERVER_BODY_DEADLINE_MS);
}

function statusOf(error: unknown): number | null {
  return error instanceof HttpApiError ? error.status : null;
}

// Tries a kept page's failed read again on SERVER_BODY_RECOVERY_DELAYS_MS.
// A success or a real answer (a 404) re-renders the page, which hands off;
// another request failure waits for the next attempt. When the last attempt
// also fails, a sample of visitors who are online report it: a failure with
// no HTTP answer is reported nowhere else, and only a human visitor, not a
// crawler refused /api/, usually stays this long. Returns the cancel.
export function recoverServerBody(
  env: ServerBodyEnvironment,
  retry: () => Promise<RetryOutcome>,
): () => void {
  let cancelled = false;
  let cancelWait = (): void => undefined;
  const attempt = (index: number): void => {
    cancelWait = env.schedule(() => {
      void retry().then((outcome) => {
        if (cancelled || !outcome.isError || !isTransientRequestFailure(outcome.error)) return;
        if (index + 1 < SERVER_BODY_RECOVERY_DELAYS_MS.length) {
          attempt(index + 1);
          return;
        }
        if (!env.isOnline() || env.sample() >= SERVER_BODY_UNRECOVERED_SAMPLE_RATE) return;
        report(env, (path) => ({
          event: 'render.server_body_unrecovered',
          path,
          status: statusOf(outcome.error),
        }));
      });
    }, SERVER_BODY_RECOVERY_DELAYS_MS[index]!);
  };
  attempt(0);
  return () => {
    cancelled = true;
    cancelWait();
  };
}

// Called by each composed route's page component with `ready` true once the
// page has nothing left to load. A layout effect, so the reveal lands in the
// same frame as the finished page and nothing paints in between.
export function useServerBodyHandoff(ready: boolean): void {
  const env = useContext(ServerBodyContext);
  useLayoutEffect(() => {
    if (ready && env !== null) handOffServerBody(env.document);
  }, [ready, env]);
}

// Called with `kept` true when the live page failed for a reason outside it,
// so the server body, which already shows that content, stays up as the
// finished page.
export function useKeepServerBody(kept: boolean): void {
  const env = useContext(ServerBodyContext);
  useLayoutEffect(() => {
    if (kept && env !== null) keepServerBody(env.document);
  }, [kept, env]);
}

// The read a composed page's server body stands in for, as its query result
// carries it.
export interface ServerBodyRead {
  readonly isError: boolean;
  readonly error: unknown;
  readonly refetch: () => Promise<RetryOutcome>;
}

// Called by a composed page with its primary read. Once a failure of that
// read has kept the server body (`LoadFailure`), reports a failure with an
// HTTP status at once (the API failing a page it had just served), then
// tries the read again (`recoverServerBody`), once per page. It lives in the
// page rather than in the failure surface because each attempt sets the read
// back to pending, which unmounts that surface until the read answers, and
// the schedule has to run across it.
export function useServerBodyRecovery(read: ServerBodyRead): void {
  const env = useContext(ServerBodyContext);
  const readRef = useRef(read);
  readRef.current = read;
  const failedOutsidePage = read.isError && isTransientRequestFailure(read.error);
  const cancelRef = useRef<(() => void) | null>(null);
  // Survives StrictMode's second run of the effects, which would report twice.
  const reportedRef = useRef(false);
  useLayoutEffect(() => {
    if (!failedOutsidePage || env === null || cancelRef.current !== null) return;
    if (!isKept(env.document)) return;
    const status = statusOf(readRef.current.error);
    if (status !== null && !reportedRef.current) {
      reportedRef.current = true;
      report(env, (path) => ({ event: 'render.server_body_kept', path, status }));
    }
    cancelRef.current = recoverServerBody(env, () => readRef.current.refetch());
  }, [failedOutsidePage, env]);
  useLayoutEffect(
    () => () => {
      cancelRef.current?.();
      cancelRef.current = null;
    },
    [],
  );
}

// Arms the deadline once `armed` (the router has mounted).
export function useServerBodyDeadline(armed: boolean): void {
  const env = useContext(ServerBodyContext);
  useLayoutEffect(() => {
    if (!armed || env === null) return undefined;
    return armServerBodyDeadline(env);
  }, [armed, env]);
}
